import { useShallow } from 'zustand/react/shallow';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChannelType,
  MessageType,
  RelationshipType,
  type Attachment,
  type Channel,
  type Gif,
  type Message,
} from '@comms/shared';
import {
  encryptFile,
  type DmMessageView,
  type EncryptedAttachmentRef,
  type GroupStatus,
} from '@comms/crypto';
import { addMessages, mentionsMe, useStore } from '../store';
import { openModal } from '../store/ui';
import { api, uploadFile } from '../lib/api';
import { getEngine, startE2ee, syncDm } from '../lib/e2ee';
import { useSession } from '../lib/session';
import { DmCallPanel } from '../components/CallUi';
import { joinCall, useVoice } from '../lib/voice';
import { MessageList, type MessageActions, type ViewMessage } from '../components/MessageList';
import { Composer, type MentionCandidate } from '../components/Composer';
import { TypingIndicator } from '../components/TypingIndicator';
import { dmTitle } from '../components/DmSidebar';
import { Avatar, Button, Icon, Modal, Spinner } from '../components/ui';
import { NavButton } from '../components/MobileNav';
import { toView as toPlainView } from './ChannelView';

type Mode = 'e2ee' | 'standard';
const EMPTY: Message[] = [];
const MODE_LABEL: Record<Mode, string> = { e2ee: 'end-to-end encrypted', standard: 'standard' };

function toView(m: DmMessageView, meId: string | undefined, all: DmMessageView[]): ViewMessage {
  const reply = m.replyTo ? all.find((x) => x.id === m.replyTo) : undefined;
  return {
    id: m.id,
    authorId: m.authorId,
    content: m.content,
    createdAt: m.createdAt,
    editedAt: m.editedAt,
    attachments: m.attachments.map((a) => ({
      id: a.id,
      url: a.url,
      filename: a.filename,
      contentType: a.contentType,
      size: a.size,
      encrypted: a,
      gif: a.gif,
    })),
    reactions: m.reactions.map((r) => ({
      emoji: r.emoji,
      count: r.userIds.length,
      me: !!meId && r.userIds.includes(meId),
    })),
    replyTo: reply ? { id: reply.id, authorId: reply.authorId, content: reply.content } : null,
    undecryptable: m.undecryptable,
  };
}

export function DmView({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const me = useStore((s) => s.me);
  const title = useStore((s) => (channel ? dmTitle(s, channel) : ''));
  const e2eeError = useSession((s) => s.e2eeError);
  const [messages, setMessages] = useState<DmMessageView[]>([]);
  const [status, setStatus] = useState<GroupStatus | 'starting'>('starting');
  const [replyTo, setReplyTo] = useState<ViewMessage | null>(null);
  const [adding, setAdding] = useState(false);
  const [switching, setSwitching] = useState(false);
  // Which history is on screen: the conversation's current mode, unless the user is
  // looking back at messages from the other mode.
  const mode: Mode = channel?.e2ee === false ? 'standard' : 'e2ee';
  const [viewing, setViewing] = useState<Mode | null>(null);
  const shown = viewing ?? mode;
  useEffect(() => setViewing(null), [mode]);
  const plain = useStore((s) => s.messages[channelId]?.items ?? EMPTY);
  const plainLoaded = useStore((s) => !!s.messages[channelId]?.loaded);
  const plainHasMore = useStore((s) => s.messages[channelId]?.hasMore ?? false);
  const voiceEnabled = useStore((s) => s.voiceEnabled);
  const inThisCall = useVoice(
    (s) => s.kind === 'dm' && s.channelId === channelId && s.status !== 'idle',
  );

  // While encryption can't start (the session retries it, and the top banner says why),
  // wait; once it recovers, this runs again and loads the conversation.
  const e2eeFailed = !!e2eeError;
  useEffect(() => {
    if (!me || e2eeFailed) return;
    let off = () => {};
    let alive = true;
    startE2ee(me.id)
      .then(async (engine) => {
        if (!alive) return;
        const reload = async () => alive && setMessages(await engine.messages(channelId));
        off = engine.on((e) => {
          if (e.channelId !== channelId) return;
          if (e.type === 'messages') void reload();
          if (e.type === 'status') setStatus(e.status);
          if (e.type === 'typing')
            useStore.setState((s) => ({
              typing: {
                ...s.typing,
                [channelId]: { ...s.typing[channelId], [e.userId]: Date.now() + 9000 },
              },
            }));
        });
        await reload();
        setStatus(engine.status.get(channelId) ?? 'syncing');
        const synced = await engine.sync(channelId).then(
          () => true,
          () => false,
        );
        setStatus(engine.status.get(channelId) ?? (synced ? 'ready' : 'error'));
      })
      .catch(() => setStatus('error'));
    return () => {
      alive = false;
      off();
    };
  }, [channelId, me, e2eeFailed]);

  // Not ready (waiting for keys, a failed sync, or set-up that never finished): keep
  // retrying while the DM is open, so it recovers once the other person's device lets it in.
  const stuck = status === 'waiting-for-keys' || status === 'error';
  useEffect(() => {
    if (status === 'ready' || status === 'starting') return;
    const t = window.setInterval(() => syncDm(channelId), 20_000);
    return () => window.clearInterval(t);
  }, [status, channelId]);

  // Standard-mode history (also shown on request while the DM is end-to-end encrypted).
  useEffect(() => {
    if (plainLoaded) return;
    api<Message[]>('GET', `/channels/${channelId}/messages?limit=50`)
      .then((ms) => addMessages(channelId, ms.reverse(), ms.length === 50))
      .catch(() => addMessages(channelId, [], false));
  }, [channelId, plainLoaded]);
  const loadOlderPlain = useCallback(async () => {
    const first = useStore.getState().messages[channelId]?.items[0];
    if (!first) return;
    const ms = await api<Message[]>(
      'GET',
      `/channels/${channelId}/messages?limit=50&before=${first.id}`,
    );
    addMessages(channelId, ms.reverse(), ms.length === 50);
  }, [channelId]);

  // Read state: ack the newest message on screen.
  const lastServerId = shown === 'e2ee' ? messages.at(-1)?.serverId : plain.at(-1)?.id;
  useEffect(() => {
    if (!lastServerId) return;
    const t = window.setTimeout(
      () =>
        !document.hidden &&
        void api('POST', `/channels/${channelId}/messages/${lastServerId}/ack`).catch(() => {}),
      500,
    );
    return () => window.clearTimeout(t);
  }, [channelId, lastServerId]);

  // Mode switches are noted in the standard history; show them in the encrypted one too.
  const modeNotes = useMemo(
    () =>
      plain.filter((m) => m.type === MessageType.DM_MODE_CHANGE).map((m) => toPlainView(m, null)),
    [plain],
  );
  const views = useMemo(
    () =>
      [...messages.map((m) => toView(m, me?.id, messages)), ...modeNotes].sort((x, y) =>
        x.createdAt.localeCompare(y.createdAt),
      ),
    [messages, me?.id, modeNotes],
  );
  const plainViews = useMemo(() => plain.map((m) => toPlainView(m, null, mentionsMe(m))), [plain]);
  const byId = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);

  const sendEnvelope = useCallback(
    async (env: Parameters<NonNullable<ReturnType<typeof getEngine>>['send']>[1]) => {
      const engine = getEngine();
      if (!engine) throw new Error('Encryption is still starting');
      await engine.send(channelId, env);
    },
    [channelId],
  );

  const actions: MessageActions = {
    onReply: setReplyTo,
    onEdit: (m, content) =>
      sendEnvelope({ v: 1, type: 'edit', target: m.id, content, sentAt: Date.now() }),
    onDelete: async (m) => {
      await sendEnvelope({ v: 1, type: 'delete', target: m.id, sentAt: Date.now() });
      // Also remove the original ciphertext from the server.
      const original = byId.get(m.id);
      if (original)
        await api('DELETE', `/channels/${channelId}/e2ee/messages/${original.serverId}`).catch(
          () => {},
        );
    },
    onReact: (m, emoji, remove) =>
      void sendEnvelope({
        v: 1,
        type: 'reaction',
        target: m.id,
        emoji,
        remove,
        sentAt: Date.now(),
      }),
  };

  const plainActions: MessageActions = {
    onReply: setReplyTo,
    onEdit: async (m, content) => {
      await api('PATCH', `/channels/${channelId}/messages/${m.id}`, { content });
    },
    onDelete: async (m) => {
      await api('DELETE', `/channels/${channelId}/messages/${m.id}`);
    },
    onReact: (m, emoji, remove) =>
      void api(
        remove ? 'DELETE' : 'PUT',
        `/channels/${channelId}/messages/${m.id}/reactions/${encodeURIComponent(emoji)}/@me`,
      ),
  };

  async function sendPlain(content: string, files: File[]) {
    const attachmentIds: string[] = [];
    for (const f of files) attachmentIds.push((await uploadFile(f, f.name)).id);
    await api('POST', `/channels/${channelId}/messages`, {
      content,
      attachmentIds,
      ...(replyTo ? { referenceId: replyTo.id } : {}),
    });
    setReplyTo(null);
  }

  async function sendPlainGif(gif: Gif) {
    await api('POST', `/channels/${channelId}/messages`, {
      gifId: gif.id,
      ...(replyTo ? { referenceId: replyTo.id } : {}),
    });
    setReplyTo(null);
  }

  async function send(content: string, files: File[]) {
    const attachments: EncryptedAttachmentRef[] = [];
    for (const f of files) {
      const enc = await encryptFile(new Uint8Array(await f.arrayBuffer()));
      const blob = new Blob([enc.ciphertext.slice().buffer as ArrayBuffer], {
        type: 'application/octet-stream',
      });
      const att: Attachment = await uploadFile(blob, f.name, { encrypted: true, channelId });
      attachments.push({
        id: att.id,
        url: att.url,
        filename: f.name,
        contentType: f.type || 'application/octet-stream',
        size: f.size,
        key: enc.key,
        iv: enc.iv,
        sha256: enc.sha256,
      });
    }
    await sendEnvelope({
      v: 1,
      type: 'message',
      id: crypto.randomUUID(),
      content,
      ...(attachments.length ? { attachments } : {}),
      ...(replyTo ? { replyTo: replyTo.id } : {}),
      sentAt: Date.now(),
    });
    setReplyTo(null);
  }

  /**
   * GIFs in encrypted conversations travel like any attachment: fetched once through our
   * server's proxy, encrypted here, and uploaded as ciphertext.
   */
  async function sendGif(gif: Gif) {
    const res = await fetch(gif.url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not load that GIF');
    const plain = new Uint8Array(await res.arrayBuffer());
    const contentType = res.headers.get('content-type') ?? 'video/mp4';
    const enc = await encryptFile(plain);
    const blob = new Blob([enc.ciphertext.slice().buffer as ArrayBuffer], {
      type: 'application/octet-stream',
    });
    const filename = `${(gif.title || 'gif').slice(0, 60)}.${contentType === 'video/webm' ? 'webm' : 'mp4'}`;
    const att: Attachment = await uploadFile(blob, filename, { encrypted: true, channelId });
    await sendEnvelope({
      v: 1,
      type: 'message',
      id: crypto.randomUUID(),
      content: '',
      attachments: [
        {
          id: att.id,
          url: att.url,
          filename,
          contentType,
          size: plain.length,
          key: enc.key,
          iv: enc.iv,
          sha256: enc.sha256,
          gif: true,
        },
      ],
      ...(replyTo ? { replyTo: replyTo.id } : {}),
      sentAt: Date.now(),
    });
    setReplyTo(null);
  }

  const mentionCandidates = useCallback(
    (q: string): MentionCandidate[] =>
      (channel?.recipients ?? [])
        .filter((u) => u.username.includes(q) || u.displayName.toLowerCase().includes(q))
        .map((u) => ({ id: u.id, label: u.displayName, sub: u.username, insert: `<@${u.id}>` })),
    [channel],
  );

  if (!channel)
    return (
      <div className="flex flex-1 items-center justify-center text-text-3">
        Conversation not found.
      </div>
    );
  const others = (channel.recipients ?? []).filter((r) => r.id !== me?.id);
  const isGroup = channel.type === ChannelType.GROUP_DM;

  const otherMode: Mode = mode === 'e2ee' ? 'standard' : 'e2ee';
  const otherHistory = mode === 'e2ee' ? plain.length > 0 : messages.length > 0;
  const historyBar =
    shown !== mode ? (
      <div className="flex items-center justify-between gap-4 bg-bg-1 px-4 py-2 text-sm text-text-1">
        <span>Showing messages sent while this conversation was {MODE_LABEL[shown]}.</span>
        <Button variant="secondary" className="py-1" onClick={() => setViewing(null)}>
          Back to the conversation
        </Button>
      </div>
    ) : otherHistory ? (
      <div className="flex items-center justify-between gap-4 bg-bg-1 px-4 py-1.5 text-xs text-text-2">
        <span>Earlier messages from {MODE_LABEL[otherMode]} mode are kept separately.</span>
        <button
          type="button"
          className="font-medium text-accent hover:underline"
          onClick={() => setViewing(otherMode)}
        >
          Show them
        </button>
      </div>
    ) : null;

  const resetEncryption = async () => {
    if (
      confirm(
        mode === 'e2ee'
          ? 'Start a fresh encrypted session for everyone in this conversation? Older messages stay readable only on devices that already decrypted them.'
          : 'Set up call encryption for this conversation again? Messages are not affected.',
      )
    ) {
      await getEngine()?.resetGroup(channelId);
    }
  };
  const callBlockedReason =
    status === 'ready'
      ? null
      : status === 'waiting-for-keys'
        ? `Calls are encrypted, and this device is still waiting for this conversation's keys from ${title}'s device.`
        : status === 'error'
          ? "Calls are encrypted, and this device couldn't set up this conversation's keys."
          : 'Setting up call encryption…';

  const banner =
    shown !== 'e2ee' ? (
      // Standard mode: messages don't need the keys, but calls do.
      voiceEnabled && !e2eeError && stuck ? (
        <div className="flex items-center justify-between gap-4 bg-warning/20 px-4 py-2 text-sm text-text-0">
          <span>
            {callBlockedReason}{' '}
            {status === 'waiting-for-keys'
              ? 'It asked for them and will keep trying.'
              : 'Trying again…'}
          </span>
          <Button variant="secondary" className="shrink-0 py-1" onClick={resetEncryption}>
            Fix calls
          </Button>
        </div>
      ) : null
    ) : status === 'error' && !e2eeError ? (
      <div className="flex items-center justify-between gap-4 bg-danger/20 px-4 py-2 text-sm text-text-0">
        <span>Encryption for this conversation ran into a problem on this device.</span>
        <Button variant="danger" className="py-1" onClick={resetEncryption}>
          Reset encryption
        </Button>
      </div>
    ) : status === 'waiting-for-keys' ? (
      <div className="bg-warning/20 px-4 py-2 text-sm text-text-0">
        Waiting for another participant's device to come online and share encryption keys with this
        device…
      </div>
    ) : null;

  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-bg-0 px-4 shadow-sm">
          <NavButton />
          {others.length === 1 ? (
            <Avatar userId={others[0]!.id} size={24} showStatus />
          ) : (
            <Icon name="users" className="text-text-3" />
          )}
          <h1 className="truncate font-semibold text-text-0">{title}</h1>
          <button
            type="button"
            onClick={() => setSwitching(true)}
            className={
              mode === 'e2ee'
                ? 'flex shrink-0 items-center gap-1 rounded bg-success/15 px-2 py-0.5 text-xs font-medium text-success hover:bg-success/25'
                : 'flex shrink-0 items-center gap-1 rounded bg-bg-3 px-2 py-0.5 text-xs font-medium text-text-2 hover:text-text-0'
            }
            title={
              mode === 'e2ee'
                ? 'Only the people in this conversation can read it. Not even the server admin. Click to change.'
                : 'Messages are stored on the server, like server channels. Click to change.'
            }
          >
            <Icon name={mode === 'e2ee' ? 'lock' : 'unlock'} className="h-3 w-3" />
            {mode === 'e2ee' ? 'End-to-end encrypted' : 'Standard'}
          </button>
          <div className="ml-auto flex items-center gap-3">
            {voiceEnabled && !inThisCall && (
              <>
                <button
                  type="button"
                  title={callBlockedReason ?? 'Start voice call'}
                  aria-label="Start voice call"
                  disabled={status !== 'ready'}
                  onClick={() => void joinCall(channelId)}
                  className="text-text-2 hover:text-text-0 disabled:opacity-40"
                >
                  <Icon name="phone" />
                </button>
                <button
                  type="button"
                  title={callBlockedReason ?? 'Start video call'}
                  aria-label="Start video call"
                  disabled={status !== 'ready'}
                  onClick={() => void joinCall(channelId, { video: true })}
                  className="text-text-2 hover:text-text-0 disabled:opacity-40"
                >
                  <Icon name="video" />
                </button>
              </>
            )}
            <button
              type="button"
              title="Verify encryption"
              onClick={() => openModal({ kind: 'safety', channelId })}
              className="text-text-2 hover:text-text-0"
            >
              <Icon name="shield" />
            </button>
            {isGroup && (
              <button
                type="button"
                title="Add people"
                onClick={() => setAdding(true)}
                className="text-text-2 hover:text-text-0"
              >
                <Icon name="plus" />
              </button>
            )}
          </div>
        </header>
        {banner}
        {historyBar}
        <DmCallPanel channelId={channelId} />
        {shown === 'standard' ? (
          <MessageList
            key="standard"
            messages={plainViews}
            guildId={null}
            actions={plainActions}
            hasMore={plainHasMore}
            loadOlder={loadOlderPlain}
            intro={
              <div>
                <div className="mb-3 flex gap-1">
                  {others.slice(0, 4).map((o) => (
                    <Avatar key={o.id} userId={o.id} size={72} />
                  ))}
                </div>
                <h2 className="text-3xl font-bold text-text-0">{title}</h2>
                <p className="mt-1 flex items-center gap-1 text-text-3">
                  <Icon name="unlock" className="h-4 w-4" /> Standard mode: messages are stored on
                  the server, so your full history is on every device.
                </p>
              </div>
            }
          />
        ) : status === 'starting' ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <MessageList
            key="e2ee"
            messages={views}
            guildId={null}
            actions={actions}
            hasMore={false}
            intro={
              <div>
                <div className="mb-3 flex gap-1">
                  {others.slice(0, 4).map((o) => (
                    <Avatar key={o.id} userId={o.id} size={72} />
                  ))}
                </div>
                <h2 className="text-3xl font-bold text-text-0">{title}</h2>
                <p className="mt-1 flex items-center gap-1 text-text-3">
                  <Icon name="lock" className="h-4 w-4" /> Messages and files here are end-to-end
                  encrypted. History is stored only on your devices.
                </p>
              </div>
            }
          />
        )}
        <TypingIndicator channelId={channelId} guildId={null} />
        <Composer
          placeholder={`Message ${isGroup ? title : `@${title}`}`}
          disabled={
            shown !== mode ||
            (shown === 'e2ee' &&
              (status === 'waiting-for-keys' || status === 'starting' || !!e2eeError))
          }
          disabledReason={
            shown !== mode
              ? `Viewing ${MODE_LABEL[shown]} history. Go back to the conversation to send.`
              : status === 'waiting-for-keys'
                ? 'Waiting for encryption keys…'
                : 'Encryption is starting…'
          }
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onSend={shown === 'standard' ? sendPlain : send}
          onSendGif={shown === 'standard' ? sendPlainGif : sendGif}
          onTyping={() => void api('POST', `/channels/${channelId}/typing`).catch(() => {})}
          mentionCandidates={mentionCandidates}
        />
      </div>
      {switching && <ModeModal channel={channel} mode={mode} onClose={() => setSwitching(false)} />}
      {adding && (
        <AddToGroupModal
          channelId={channelId}
          existing={(channel.recipients ?? []).map((r) => r.id)}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  );
}

function ModeModal({
  channel,
  mode,
  onClose,
}: {
  channel: Channel;
  mode: Mode;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const target: Mode = mode === 'e2ee' ? 'standard' : 'e2ee';
  async function switchMode() {
    setBusy(true);
    setError(null);
    try {
      const updated = await api<Channel>('PATCH', `/channels/${channel.id}`, {
        e2ee: target === 'e2ee',
      });
      useStore.setState((s) => ({
        channels: { ...s.channels, [updated.id]: { ...s.channels[updated.id], ...updated } },
      }));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch');
      setBusy(false);
    }
  }
  return (
    <Modal title="Conversation mode" onClose={onClose}>
      <div className="space-y-3 text-sm text-text-1">
        <div
          className={
            mode === 'e2ee'
              ? 'rounded border border-success/50 p-3'
              : 'rounded border border-bg-3 p-3'
          }
        >
          <div className="flex items-center gap-1 font-semibold text-text-0">
            <Icon name="lock" className="h-4 w-4" /> End-to-end encrypted
            {mode === 'e2ee' && <span className="ml-1 text-xs text-success">(current)</span>}
          </div>
          <p className="mt-1 text-text-2">
            Only the people in this conversation can read it, not even the server admin. Messages
            wait on the server, encrypted, until the others come online. A device that signs in
            later only sees messages from then on.
          </p>
        </div>
        <div
          className={
            mode === 'standard'
              ? 'rounded border border-accent/60 p-3'
              : 'rounded border border-bg-3 p-3'
          }
        >
          <div className="flex items-center gap-1 font-semibold text-text-0">
            <Icon name="unlock" className="h-4 w-4" /> Standard
            {mode === 'standard' && <span className="ml-1 text-xs text-accent">(current)</span>}
          </div>
          <p className="mt-1 text-text-2">
            Messages are stored on the server like server channels: protected in transit and on
            disk, the full history is on every device and searchable, but the server admin could
            read them.
          </p>
        </div>
        <p className="text-text-3">
          Switching applies to everyone in this conversation and is announced in it. Messages
          already sent stay where they are; you can still view them.
        </p>
        {error && <p className="text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void switchMode()} disabled={busy}>
            {target === 'e2ee' ? 'Turn on end-to-end encryption' : 'Switch to standard'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AddToGroupModal({
  channelId,
  existing,
  onClose,
}: {
  channelId: string;
  existing: string[];
  onClose: () => void;
}) {
  const friends = useStore(
    useShallow((s) =>
      Object.values(s.relationships).filter(
        (r) => r.type === RelationshipType.FRIEND && !existing.includes(r.id),
      ),
    ),
  );
  return (
    <Modal title="Add people" onClose={onClose}>
      {friends.length === 0 && <p className="text-sm text-text-3">No other friends to add.</p>}
      {friends.map((f) => (
        <div key={f.id} className="flex items-center gap-3 py-1.5">
          <Avatar userId={f.id} size={32} />
          <span className="flex-1 text-text-1">{f.user.displayName}</span>
          <Button
            className="py-1"
            onClick={async () => {
              await api('PUT', `/channels/${channelId}/recipients/${f.id}`);
              onClose();
            }}
          >
            Add
          </Button>
        </div>
      ))}
    </Modal>
  );
}
